import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  SELECTION_CHANGE_COMMAND,
} from 'lexical';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { MessageSquare } from '@lifeomic/chromicons';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { defineMessages, useIntl } from 'react-intl';

const messages = defineMessages({
  addAnnotation: {
    id: 'FloatingAnnotationButton.addAnnotation',
    defaultMessage: 'Add annotation',
  },
});

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    position: 'absolute',
    zIndex: 100,
    boxShadow: theme.boxShadows.table,
    borderRadius: theme.pxToRem(20),
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

type FloatingAnnotationButtonProps = {
  anchorElement: HTMLElement;
  onAddAnnotation: () => void;
};

export const FloatingAnnotationButton: React.FC<
  FloatingAnnotationButtonProps
> = ({ anchorElement, onAddAnnotation }) => {
  const [editor] = useLexicalComposerContext();
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const buttonRef = useRef<HTMLDivElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );

  const updatePosition = useCallback(() => {
    const editorState = editor.getEditorState();
    editorState.read(() => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        setCoords(null);
        return;
      }

      const domSelection = window.getSelection();
      if (!domSelection || domSelection.rangeCount === 0) {
        setCoords(null);
        return;
      }

      // Create a collapsed range at the focus (end-of-drag) point so the
      // button appears right after the last character, even when the
      // selection spans multiple lines.
      const focusRange = document.createRange();
      focusRange.setStart(domSelection.focusNode!, domSelection.focusOffset);
      focusRange.collapse(true);
      const rect = focusRange.getBoundingClientRect();

      // Fallback: if the collapsed range returns a zero rect (can happen at
      // line boundaries), fall back to the full selection rect's bottom-right.
      if (rect.width === 0 && rect.height === 0 && rect.top === 0) {
        const fullRect = domSelection.getRangeAt(0).getBoundingClientRect();
        const anchorRect = anchorElement.getBoundingClientRect();
        setCoords({
          top: fullRect.bottom - anchorRect.top + 4,
          left: fullRect.right - anchorRect.left,
        });
        return;
      }

      const anchorRect = anchorElement.getBoundingClientRect();
      setCoords({
        top: rect.top - anchorRect.top + rect.height + 4,
        left: rect.left - anchorRect.left,
      });
    });
  }, [editor, anchorElement]);

  const rafRef = useRef(0);
  const scheduleUpdate = useCallback(() => {
    cancelAnimationFrame(rafRef.current);
    rafRef.current = requestAnimationFrame(() => updatePosition());
  }, [updatePosition]);

  useEffect(() => {
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  useEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        scheduleUpdate();
        return false;
      },
      COMMAND_PRIORITY_LOW,
    );
  }, [editor, scheduleUpdate]);

  useEffect(() => {
    scheduleUpdate();
  }, [scheduleUpdate]);

  if (!coords) {
    return null;
  }

  return createPortal(
    <div
      ref={buttonRef}
      className={classes.root}
      style={{ top: coords.top, left: coords.left }}
    >
      <IconButton
        aria-label={formatMessage(messages.addAnnotation)}
        icon={MessageSquare}
        onClick={onAddAnnotation}
        size={0}
        color="default"
      />
    </div>,
    anchorElement,
  );
};
